<template>
  <!--
    移动端底部按钮组。

    ★ 与 MobileButton 配套使用，直接嵌套即可：
        <mobile-button-group>
          <mobile-button icon="..." @click="...">取消</mobile-button>
          ...
        </mobile-button-group>
      MobileButton 挂载时自动注册、卸载时自动注销，本组件按注册数量决定排版。
    ★ 三个及以上：最后两个拼成一段双色按钮，其余（取消固定置首）收成单色图标按钮；
      两个及以下：保持普通等宽按钮。
  -->
  <div class="mobile-button-group" :class="{grouped: isGrouped}">
    <!-- 普通模式直接展示子按钮；分组模式下隐藏，改由下方按注册信息重排 -->
    <div v-show="!isGrouped" class="button-slot">
      <slot/>
    </div>
    <template v-if="isGrouped">
      <div v-for="button in headButtons" :key="button.uid"
           class="head-action" :class="['type-' + headType(button.elType), {disabled: button.disabled}]"
           @click="trigger(button)">
        <yq-icon class="icon" :name="button.icon || 'element-ui_more'"/>
        <span class="label">{{ button.label }}</span>
      </div>
      <div class="split-action">
        <div class="split-part secondary"
             :class="['type-' + buttonType(tailButtons[0].elType), {disabled: tailButtons[0].disabled}]"
             @click="trigger(tailButtons[0])">{{ tailButtons[0].label }}</div>
        <div class="split-part primary"
             :class="['type-' + buttonType(tailButtons[1].elType), {disabled: tailButtons[1].disabled}]"
             @click="trigger(tailButtons[1])">{{ tailButtons[1].label }}</div>
      </div>
    </template>
  </div>
</template>

<script>

import YqIcon from "@/components/YqIcon.vue";

const BUTTON_TYPES = ["primary", "success", "warning", "danger", "info"];

export default {
  name: "MobileButtonGroup",
  components: {
    YqIcon
  },
  data() {
    return {
      // MobileButton 注册上来的元数据：{uid, label, icon, elType, disabled, click, order}
      buttons: [],
      seed: 0,
    };
  },
  computed: {
    /** 按注册顺序排列，并把「取消」固定挪到第一位 */
    orderedButtons() {
      const list = this.buttons.slice().sort((a, b) => a.order - b.order);
      const cancelIndex = list.findIndex(button => this.isCancel(button));
      if (cancelIndex > 0) {
        const [cancel] = list.splice(cancelIndex, 1);
        list.unshift(cancel);
      }
      return list;
    },
    isGrouped() {
      return this.orderedButtons.length >= 3;
    },
    headButtons() {
      return this.isGrouped ? this.orderedButtons.slice(0, this.orderedButtons.length - 2) : [];
    },
    tailButtons() {
      return this.isGrouped ? this.orderedButtons.slice(-2) : [];
    },
  },
  methods: {
    /** 子按钮挂载时注册 */
    registerButton(button) {
      if (!button || this.buttons.some(item => item.uid === button.uid)) {
        return;
      }
      button.order = ++this.seed;
      this.buttons.push(button);
    },
    /** 子按钮卸载时注销 */
    unregisterButton(uid) {
      this.buttons = this.buttons.filter(item => item.uid !== uid);
    },
    isCancel(button) {
      return (button.label || "").replace(/\s/g, "") === "取消";
    },
    /** 图标按钮：没有 el-type 时用默认文字色，有则用对应主题色（仍为单色） */
    headType(type) {
      return BUTTON_TYPES.indexOf(type) > -1 ? type : "default";
    },
    /** 双色按钮：没有 el-type 时按主色处理 */
    buttonType(type) {
      return BUTTON_TYPES.indexOf(type) > -1 ? type : "primary";
    },
    /** 点击重排后的按钮：回调子按钮自身的 handleClick，@click 仍从调用方页面抛出 */
    trigger(button) {
      if (!button || button.disabled) {
        return;
      }
      if (typeof button.click === "function") {
        button.click();
      }
      this.$emit("click", button);
    },
  }
};
</script>

<style lang="scss" scoped>
.mobile-button-group{
  display: flex;
  gap: 10px;
  width: 100%;
  .button-slot{
    flex: 1;
    display: flex;
    gap: 10px;
    min-width: 0;
    :deep(.mobile-button){
      flex: 1;
      min-width: 0;
    }
  }
}
.mobile-button-group.grouped{
  align-items: stretch;
  gap: 14px;
  .head-action{
    flex: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 0 4px;
    color: var(--yq-font-color-base);
    .icon{
      font-size: 22px;
      line-height: 1;
    }
    .label{
      margin-top: 4px;
      font-size: 11px;
      line-height: 1.2;
      white-space: nowrap;
    }
    &.type-primary{ color: var(--el-color-primary); }
    &.type-success{ color: var(--el-color-success); }
    &.type-warning{ color: var(--el-color-warning); }
    &.type-danger{ color: var(--el-color-danger); }
    &.type-info{ color: var(--el-color-info); }
    &.disabled{
      opacity: .5;
      pointer-events: none;
    }
  }
  .split-action{
    flex: 1;
    display: flex;
    min-width: 0;
    height: 50px;
    border-radius: 8px;
    overflow: hidden;
    .split-part{
      display: flex;
      align-items: center;
      justify-content: center;
      min-width: 0;
      padding: 0 12px;
      font-size: var(--yq-font-size-large-mobile);
      white-space: nowrap;
      &.secondary{
        flex: 1;
        &.type-primary{
          color: var(--el-color-primary);
          background: var(--el-color-primary-light-9);
        }
        &.type-success{
          color: var(--el-color-success);
          background: var(--el-color-success-light-9);
        }
        &.type-warning{
          color: var(--el-color-warning);
          background: var(--el-color-warning-light-9);
        }
        &.type-danger{
          color: var(--el-color-danger);
          background: var(--el-color-danger-light-9);
        }
        &.type-info{
          color: var(--el-color-info);
          background: var(--el-fill-color-light);
        }
      }
      &.primary{
        flex: 1.2;
        color: #FFF;
        &.type-primary{ background: var(--el-color-primary); }
        &.type-success{ background: var(--el-color-success); }
        &.type-warning{ background: var(--el-color-warning); }
        &.type-danger{ background: var(--el-color-danger); }
        &.type-info{ background: var(--el-color-info); }
      }
      &.disabled{
        opacity: .5;
        pointer-events: none;
      }
    }
  }
}
</style>
